<script setup>

    import { computed, ref, watch } from 'vue'

    const emit = defineEmits([
        'change:criteria-attribute-choice',
        'change:criteria-attribute-no-choice',
        'change:criteria-attribute-all-choice'
    ])

    const props = defineProps({
        criteria_descriptor: {
            type: Object,
            required: true,
        },
        criteria_selected_attributes: {
            type: [Object, undefined],
            required: true,
        },
        criteria_descriptor_name_dict: {
            type: Object,
            required: true,
        },
        selected: {
            type: Boolean,
            default: false,
        }
    })

    function handleSelectCriteriaAttributeMultiple(criteriaNameId, attributeNameId, choiceId) {
        emit('change:criteria-attribute-choice',
            criteriaNameId,
            attributeNameId,
            choiceId
        )
    }

    function handleSelectCriteriaAttributeNoChoice(criteriaNameId, attributeNameId) {
        emit('change:criteria-attribute-no-choice',
            criteriaNameId,
            attributeNameId
        )
    }

    function handleSelectCriteriaAttributeAllChoice(criteriaNameId, attributeNameId) {
        emit('change:criteria-attribute-all-choice',
            criteriaNameId,
            attributeNameId
        )
    }

    function handleCheckboxSelectCriteria(criteriaNameId, attributeNameId, event) {
        if (event.target.type == "checkbox" && event.target.checked == false) {
            delete singleChoiceSelected.value[attributeNameId]
        }
        emit('change:criteria-attribute-choice',
            criteriaNameId,
            attributeNameId,
            singleChoiceSelected.value[attributeNameId] ? singleChoiceSelected.value[attributeNameId] : null
        )
    }

    const allChoiceChecked = computed(() => {
        const checked = {}

        props.criteria_descriptor.attributes.forEach(attribute => {
            if (props.criteria_selected_attributes[attribute.name_id] === undefined) {
                checked[attribute.name_id] = false
            } else {
                checked[attribute.name_id] = props.criteria_descriptor_name_dict[props.criteria_descriptor.name_id].attribute_dict[attribute.name_id].choices.length
                    ===
                props.criteria_selected_attributes[attribute.name_id].selected_choice_ids.length
            }
        })

        return checked
    })

    const singleChoiceSelected = ref({})
    watch(() => props.criteria_selected_attributes, (newVal) => {
        props.criteria_descriptor.attributes.forEach(attribute => {
            if (attribute.can_caregiver_select_multiple === false) {
                singleChoiceSelected.value[attribute.name_id] = newVal[attribute.name_id].selected_choice_ids[0]
            }
        })
    }, { immediate: true })

    watch(singleChoiceSelected, (newVal) => {
        Object.keys(newVal).forEach(attributeNameId => {
            if (newVal[attributeNameId] !== props.criteria_selected_attributes[attributeNameId].selected_choice_ids[0]) {
                handleSelectCriteriaAttributeMultiple(props.criteria_descriptor.name_id, attributeNameId, newVal[attributeNameId])
            }
        })
    }, { deep: true })

    const severityCriteriaNameIds = ['alzheimers_disease', 'dementia']

    const checkboxClasses = {
        base: 'group focus:ring-lifeworx-blue-600 focus:ring-opacity-30 hover:focus:ring-opacity-50 rounded-sm cursor-pointer text-lifeworx-blue-600 border-stone-400 group-hover:border-lifeworx-blue-600',
        checked: 'group-hover:bg-lifeworx-blue-700',
        unchecked: 'group-hover:bg-[#d8e7ee] group-hover:text-lifeworx-blue-600'
    }

    const labelClasses = {
        base: 'group pl-1.5 pr-2 py-1 text-xs cursor-pointer text-lifeworx-blue-800/90 group-hover:text-lifeworx-blue-900/100',
        floating: 'group text-2xs font-semibold tracking-wide cursor-default group-hover:text-lifeworx-blue-900',
        checked: 'text-lifeworx-blue-900',
    }

    function renderChoiceLabel(choice) {
        return choice.caregiver_override_label ? choice.caregiver_override_label : choice.label
    }

</script>

<template>
    <div
        v-if="criteria_descriptor.attributes.length == 0"
        class="mr-4 content-center max-h-8 justify-self-center"
    >
        <div class="group h-fit w-fit cursor-pointer inline-flex items-center">
            <input
                type="checkbox"
                :id="`${criteria_descriptor.name_id}`"
                @click.stop="() => handleSelectCriteriaAttributeMultiple(criteria_descriptor.name_id, null, null)"
                :checked="selected"
                :class="[
                    checkboxClasses.base,
                    {
                        [checkboxClasses.checked]: selected,
                        [checkboxClasses.unchecked]: !selected
                    }
                ]"
            />
            <label
                :for="`${criteria_descriptor.name_id}`"
                :class="[
                    labelClasses.base,
                    'inline-flex text-wrap leading-tight max-w-44 overflow-hidden',
                    {
                        [labelClasses.checked]: selected,
                    }
                ]"
            >{{ criteria_descriptor.label }}</label>
        </div>
    </div>
    <template
        v-for="(attribute, i) in criteria_descriptor.attributes"
        :key="attribute.name_id"
    >
        <template v-if="severityCriteriaNameIds.includes(criteria_descriptor.name_id) === false">
            <div
                v-if="attribute.can_caregiver_select_multiple === false"
                class="flex gap-2"
            >
                <div
                    v-if="attribute.caregiver_no_choice_label"
                    class="group mr-4 h-full min-w-24 place-content-center"
                >
                    <input
                        type="checkbox"
                        :id="`${criteria_descriptor.name_id}-no-choice`"
                        @click.stop="() => handleSelectCriteriaAttributeNoChoice(criteria_descriptor.name_id, attribute.name_id)"
                        :checked="criteria_selected_attributes[attribute.name_id].caregiver_no_choice_selected"
                        :class="[
                            checkboxClasses.base,
                            {
                                [checkboxClasses.checked]: criteria_selected_attributes[attribute.name_id].caregiver_no_choice_selected,
                                [checkboxClasses.unchecked]: !criteria_selected_attributes[attribute.name_id].caregiver_no_choice_selected
                            }
                        ]"
                    />
                    <label
                        :for="`${criteria_descriptor.name_id}-no-choice`"
                        :class="[
                            labelClasses.base,
                            'text-xs font-bold cursor-pointer text-lifeworx-blue-950',
                            {
                                [labelClasses.checked]: selected,
                            }
                        ]"
                    >{{ attribute.caregiver_no_choice_label }}</label>
                </div>
                <div
                    class="flex flex-col gap-1 mr-4"
                    v-if="attribute.choices.length > 1"
                >
                    <label
                        :for="`${criteria_descriptor.name_id}-${attribute.name_id}`"
                        :class="[
                            labelClasses.floating,
                            'text-2xs tracking-wide text-lifeworx-blue-800 mb-0.5'
                        ]"
                    >{{ attribute.description_label }}</label>
                    <select
                        v-model="singleChoiceSelected[attribute.name_id]"
                        :id="`${criteria_descriptor.name_id}-${attribute.name_id}`"
                        class="text-xs font-semibold relative w-full min-w-40 pl-2.5 h-[34px] pr-10 py-7 md:py-1.5 rounded-md tracking-wider border border-stone-400 shadow-sm focus:border-lifeworx-blue-600 hover:border-lifeworx-blue-600 focus:ring-lifeworx-blue-600 focus:ring-1 focus:outline-none cursor-pointer text-lifeworx-blue-700"
                    >
                        <option
                            v-for="(choice, i) in attribute.choices"
                            :key="`${criteria_descriptor.name_id}-${attribute.name_id}-${i}`"
                            :value="choice.id"
                        >{{ renderChoiceLabel(choice) }}</option>
                    </select>
                </div>

                <div
                    v-else
                    class="place-content-center"
                >
                    <div class="group h-fit w-fit cursor-pointer">
                        <input
                            type="checkbox"
                            :id="`${criteria_descriptor.name_id}-${attribute.name_id}`"
                            :value="attribute.choices[0].id"
                            @click.stop="() => handleSelectCriteriaAttributeMultiple(criteria_descriptor.name_id, attribute.name_id, attribute.choices[0].id)"
                            :checked="criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(attribute.choices[0].id)"
                            :class="[
                                checkboxClasses.base,
                                {
                                    [checkboxClasses.checked]: criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(attribute.choices[0].id),
                                    [checkboxClasses.unchecked]: !criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(attribute.choices[0].id)
                                }
                            ]"
                        />
                        <label
                            :for="`${criteria_descriptor.name_id}-${attribute.name_id}`"
                            :class="[
                                labelClasses.base,
                                {
                                    [labelClasses.checked]: criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(attribute.choices[0].id),
                                }
                            ]"
                        >{{ renderChoiceLabel(attribute.choices[0]) }}</label>
                    </div>
                </div>
            </div>

            <div
                v-if="attribute.can_caregiver_select_multiple === true"
                class="flex flex-wrap gap-2"
            >
                <span
                    v-if="attribute.description_label"
                    :class="[
                        labelClasses.floating,
                        'flex basis-full text-2xs font-semibold text-lifeworx-blue-800 -mb-1'
                    ]"
                >
                    {{ attribute.description_label }}
                </span>

                <div
                    v-if="attribute.caregiver_all_choice_label"
                    :class="[
                        'group mr-4 content-center max-h-8 justify-self-center',
                        {
                            'pointer-events-none': allChoiceChecked[attribute.name_id]
                        }
                    ]"
                >
                    <input
                        type="checkbox"
                        :id="`${criteria_descriptor.name_id}-all-choice`"
                        @click.stop="() => handleSelectCriteriaAttributeAllChoice(criteria_descriptor.name_id, attribute.name_id)"
                        :checked="allChoiceChecked[attribute.name_id]"
                        :disabled="allChoiceChecked[attribute.name_id]"
                        :class="[
                            checkboxClasses.base,
                            {
                                [checkboxClasses.checked + ' text-stone-400']: allChoiceChecked[attribute.name_id],
                                [checkboxClasses.unchecked]: !allChoiceChecked[attribute.name_id]
                            }
                        ]"
                    />
                    <label
                        :for="`${criteria_descriptor.name_id}-all-choice`"
                        :class="[
                            labelClasses.base,
                            'text-xs font-bold cursor-pointer',
                            {
                                [labelClasses.checked + ' text-stone-600/80']: allChoiceChecked[attribute.name_id],
                            }
                        ]"
                    >{{ attribute.caregiver_all_choice_label }}</label>
                </div>
                <div
                    v-if="attribute.caregiver_no_choice_label"
                    class="mr-4 flex-auto content-center max-h-8 justify-self-center"
                >
                    <input
                        type="checkbox"
                        :id="`${criteria_descriptor.name_id}-no-choice`"
                        @click.stop="() => handleSelectCriteriaAttributeNoChoice(criteria_descriptor.name_id, attribute.name_id)"
                        :checked="criteria_selected_attributes[attribute.name_id].caregiver_no_choice_selected"
                        :class="[
                            checkboxClasses.base,
                            {
                                [checkboxClasses.checked]: criteria_selected_attributes[attribute.name_id].caregiver_no_choice_selected,
                                [checkboxClasses.unchecked]: !criteria_selected_attributes[attribute.name_id].caregiver_no_choice_selected
                            }
                        ]"
                    />
                    <label
                        :for="`${criteria_descriptor.name_id}-no-choice`"
                        class="text-xs font-semibold cursor-pointer text-lifeworx-blue-950"
                    >{{ attribute.caregiver_no_choice_label }}</label>
                </div>
                    <template
                        v-for="(choice, i) in attribute.choices"
                        :key="`${criteria_descriptor.name_id}-${attribute.name_id}-${choice.i}`"
                    >
                        <div class="mr-4 group content-center max-h-8 justify-self-center">
                            <input
                                type="checkbox"
                                :id="`${criteria_descriptor.name_id}_${attribute.name_id}_${choice.id}`"
                                :value="choice.id"
                                :checked="criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(choice.id)"
                                @click.stop="() => handleSelectCriteriaAttributeMultiple(criteria_descriptor.name_id, attribute.name_id, choice.id)"
                                :class="[
                                    checkboxClasses.base,
                                    {
                                        [checkboxClasses.checked]: criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(choice.id),
                                        [checkboxClasses.unchecked]: !criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(choice.id)
                                    }
                                ]"
                            />
                            <label
                                :for="`${criteria_descriptor.name_id}_${attribute.name_id}_${choice.id}`"
                                :class="[
                                    labelClasses.base,
                                    {
                                        [labelClasses.checked]: criteria_selected_attributes[attribute.name_id].selected_choice_ids.includes(choice.id),
                                    }
                                ]"
                            ><template v-if="criteria_descriptor.name_id == 'catheter'">Catheter - </template>{{ renderChoiceLabel(choice) }}</label>
                        </div>
                    </template>
            </div>
        </template>
        <template v-else>

            <div
                class="mr-4 -mb-1 -mt-1.5 flex flex-col gap-y-1"
            >
                <label
                    :for="`${criteria_descriptor.name_id}-${attribute.name_id}`"
                    :class="[
                        labelClasses.floating,
                        {
                            'text-lifeworx-blue-800': !selected || singleChoiceSelected[attribute.name_id],
                            'text-lifeworx-red-500': selected && !singleChoiceSelected[attribute.name_id] && attribute.name_id == criteria_descriptor.name_id + '_severity',
                        }
                    ]"
                >
                    <span class="font-semibold">{{ criteria_descriptor.label }}</span>
                    - {{ attribute.description_label }}
                </label>

                <div
                    :class="[
                        'flex relative w-auto min-w-40 pl-2.5 rounded-md overflow-clip tracking-wider border text-sm shadow-sm hover:border-lifeworx-blue-600 focus:border-lifeworx-blue-600 focus:ring-lifeworx-blue-600 focus:ring-1 focus:outline-none group cursor-pointer',
                        {
                            'bg-lifeworx-red-500/5 border-lifeworx-red-500 backdrop-brightness-125': selected && !singleChoiceSelected[attribute.name_id] && attribute.name_id == criteria_descriptor.name_id + '_severity',
                            'bg-white border-stone-400/50': !selected || singleChoiceSelected[attribute.name_id],
                        }
                    ]"
                    @click.stop="(event) => handleCheckboxSelectCriteria(criteria_descriptor.name_id, null, event)"
                >
                    <div class="flex w-full mb-4 md:mb-0">

                        <input
                            type="checkbox"
                            :id="`${criteria_descriptor.name_id}`"
                            :checked="selected"
                            :class="[
                                'flex justify-center self-center mr-1 focus:ring-0  focus:bg-lifeworx-blue-600 focus:bg-opacity-50  rounded-sm cursor-pointer ',
                                {
                                    'text-lifeworx-red-500 group-hover:text-lifeworx-blue-600': selected && !singleChoiceSelected[attribute.name_id] && attribute.name_id == criteria_descriptor.name_id + '_severity',
                                    'text-lifeworx-blue-600 checked:bg-lifeworx-blue-600 group-hover:border-lifeworx-blue-600 group-hover:bg-[#d8e7ee]': !selected || singleChoiceSelected[attribute.name_id],
                                }
                            ]"
                            @click.stop="(event) => handleCheckboxSelectCriteria(criteria_descriptor.name_id, null, event)"
                        />

                        <select
                            :id="`${criteria_descriptor.name_id}-${attribute.name_id}`"
                            v-model="singleChoiceSelected[attribute.name_id]"
                            ref="severitySelect"
                            :disabled="attribute.choices.length === 0"
                            :class="[
                                'flex w-full m-0 pl-2.5 pr-10 md:px-2.5 py-7 md:py-1.5 tracking-wider border-none text-xs font-semibold ring-0 outline-none focus:outline-none focus:ring-0 focus:border-none disabled:cursor-default cursor-pointer bg-transparent',
                            ]"
                            @click.stop
                        >
                            <option
                                v-for="(option, index) in attribute.choices"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ renderChoiceLabel(option) }}
                            </option>
                        </select>

                    </div>
                </div>

            </div>

        </template>

    </template>
</template>
